import { containsIgnoreCase } from "../../utils/stringUtils";
import { formatYmd, todayYmd } from "../../features/cases/dates";
import { POINIKA_ACTIONS } from "../../features/cases/listActions";
import { eisagLabel } from "../../features/poinika/eisaggelies";
import { Ellipsis, formatServerDate, parseServerDate } from "../cases/caseColumns";
import UpcomingOverview from "../cases/UpcomingOverview";
import AddPoinikoForm from "./AddPoinikoForm";
import PoinikoPanelBody from "./PoinikoPanelBody";
import { hearingsOf, nextHearing } from "./poinikaData";

const field = (key) => (c) => nextHearing(c)?.[key] ?? "";

const COLUMNS = [
  {
    id: "abm",
    label: "ΑΒΜ",
    width: 110,
    defaultVisible: true,
    sortValue: (c) => c.abm || "",
    render: (c) => <span className="ds-strong">{c.abm}</span>,
  },
  {
    id: "note",
    flex: true,
    label: "Σημείωση",
    width: 200,
    defaultVisible: true,
    sortValue: (c) => c.note?.trim() || "",
    render: (c) => <Ellipsis>{c.note?.trim() || ""}</Ellipsis>,
  },
  {
    id: "eisag",
    flex: true,
    label: "Εισαγγελία",
    width: 150,
    defaultVisible: true,
    sortValue: (c) => eisagLabel(c.eisag),
    render: (c) => <Ellipsis title={c.eisag}>{eisagLabel(c.eisag)}</Ellipsis>,
  },
  {
    id: "next_date",
    label: "Δικάσιμος",
    width: 100,
    defaultVisible: true,
    sortValue: (c) => nextHearing(c)?.ymd ?? null,
    render: (c) => {
      const h = nextHearing(c);
      if (!h) return null;
      const upcoming = h.ymd && h.ymd >= todayYmd();
      return (
        <span className={upcoming ? "ds-date-upcoming" : "ds-muted"}>
          {h.ymd ? formatYmd(h.ymd) : h.date}
        </span>
      );
    },
  },
  {
    id: "court",
    flex: true,
    label: "Δικαστήριο",
    width: 160,
    defaultVisible: true,
    sortValue: field("court"),
    render: (c) => <Ellipsis>{field("court")(c)}</Ellipsis>,
  },
  {
    id: "hearings",
    label: "Δικάσιμοι",
    width: 80,
    defaultVisible: true,
    sortValue: (c) => hearingsOf(c).length,
    render: (c) => <span className="ds-muted">{hearingsOf(c).length || ""}</span>,
  },
  { id: "ora", label: "Ώρα", width: 70 },
  { id: "room", label: "Κτήριο", width: 140, flex: true },
  { id: "aa", label: "Α/Α", width: 60 },
  { id: "pinakio", label: "Πινάκιο", width: 100 },
  { id: "gad", label: "ΓΑΔ", width: 110 },
  { id: "tropos", label: "Τρόπος εισαγωγής", width: 160, flex: true },
  { id: "eidos", label: "Διαδικασία", width: 160, flex: true },
  {
    id: "created",
    label: "Καταχώρηση",
    width: 120,
    defaultVisible: false,
    sortValue: (c) => parseServerDate(c.created_at)?.getTime() ?? null,
    render: (c) => <span className="ds-muted">{formatServerDate(c.created_at)}</span>,
  },
].map((col) =>
  col.render
    ? col
    : {
        // Fields of the next hearing, hidden by default.
        ...col,
        defaultVisible: false,
        sortValue: field(col.id),
        render: (c) => <Ellipsis>{field(col.id)(c)}</Ellipsis>,
      }
);

const search = (c, q) =>
  containsIgnoreCase(c.abm, q) ||
  containsIgnoreCase(c.note, q) ||
  containsIgnoreCase(c.eisag, q) ||
  containsIgnoreCase(eisagLabel(c.eisag), q) ||
  hearingsOf(c).some((h) => containsIgnoreCase(h.court, q) || containsIgnoreCase(h.gad, q));

const FILTER_KEYS = ["eisag", "hearings"];

const getFilterDefs = (rows) => [
  {
    key: "eisag",
    label: "Εισαγγελία",
    options: [...new Set(rows.map((c) => c.eisag).filter(Boolean))]
      .sort((a, b) => eisagLabel(a).localeCompare(eisagLabel(b), "el"))
      .map((v) => ({ value: v, label: eisagLabel(v) })),
  },
  {
    key: "hearings",
    label: "Δικάσιμοι",
    options: [
      { value: "upcoming", label: "Με επερχόμενη δικάσιμο" },
      { value: "yes", label: "Με δικασίμους" },
      { value: "no", label: "Χωρίς δικασίμους" },
    ],
  },
];

const applyFilters = (rows, { eisag, hearings }) =>
  rows.filter((c) => {
    if (eisag && c.eisag !== eisag) return false;
    if (hearings) {
      const list = hearingsOf(c);
      if (hearings === "no" && list.length) return false;
      if (hearings === "yes" && !list.length) return false;
      if (hearings === "upcoming" && !list.some((h) => h.ymd >= todayYmd())) return false;
    }
    return true;
  });

const getEvents = (c) => hearingsOf(c).map((h) => ({ ymd: h.ymd }));
const getLabel = (c) => ({
  primary: c.note?.trim() || `ΑΒΜ ${c.abm}`,
  secondary: [c.note?.trim() ? `ΑΒΜ ${c.abm}` : null, nextHearing(c)?.court]
    .filter(Boolean)
    .join(" · "),
});

const Overview = ({ rows, onOpen }) => (
  <UpcomingOverview
    rows={rows}
    getEvents={getEvents}
    getLabel={getLabel}
    onOpen={onOpen}
    title="Επερχόμενες δικάσιμοι"
    stats={[
      { label: "ΑΒΜ", value: rows.length },
      {
        label: "με επερχόμενη δικάσιμο",
        value: rows.filter((c) => hearingsOf(c).some((h) => h.ymd >= todayYmd())).length,
      },
    ]}
  />
);

const title = (c) => (c?.note?.trim() ? c.note : c ? `ΑΒΜ ${c.abm}` : "");
const subtitle = (c) =>
  c
    ? [c.note?.trim() ? `ΑΒΜ ${c.abm}` : null, eisagLabel(c.eisag)].filter(Boolean).join(" · ")
    : "";

const AddForm = ({ onClose, onAdded }) => <AddPoinikoForm onClose={onClose} onAdded={onAdded} />;

export const POINIKA_CONFIG = {
  scope: "poinika",
  title: "Πιν. Ποινικών",
  noun: { one: "ΑΒΜ", many: "ΑΒΜ", this: "αυτή την εγγραφή" },
  searchPlaceholder: "Αναζήτηση (ΑΒΜ, σημείωση, εισαγγελία, δικαστήριο)",
  addLabel: "Νέο ΑΒΜ",
  addedText: "Το ΑΒΜ καταχωρήθηκε. Οι δικάσιμοι εμφανίζονται μόλις βρεθούν.",
  emptyText: "Δεν έχετε καταχωρήσει ΑΒΜ προς έλεγχο.",
  columns: COLUMNS,
  columnsById: Object.fromEntries(COLUMNS.map((c) => [c.id, c])),
  getRowLabel: (c) => `ΑΒΜ ${c.abm}`,
  renderStatus: null,
  search,
  filterKeys: FILTER_KEYS,
  getFilterDefs,
  applyFilters,
  actions: POINIKA_ACTIONS,
  noteInPanel: true,
  detail: {
    endpoints: null,
    fullPageHref: null,
    getTitle: title,
    getSubtitle: subtitle,
    getRefreshKey: () => "",
    documentTitle: (c) => `${title(c)} | Πιν. Ποινικών | MySolon.gr`,
    renderBody: ({ data }) => <PoinikoPanelBody data={data} />,
    ariaLabel: "Στοιχεία ΑΒΜ",
  },
  Overview,
  AddForm,
  afterAdd: { refreshDelays: [0, 1000, 5000] },
};
